import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { 
  User, 
  Shield, 
  Clock, 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Loader2,
  Activity
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';

interface AuthEvent {
  event: string;
  timestamp: Date;
  userId?: string;
}

const AuthDebugSection = () => {
  const { user, session, role, isAdmin, loading, lastAuthEvent } = useAuth();
  const [refreshing, setRefreshing] = useState(false);
  const [authEvents, setAuthEvents] = useState<AuthEvent[]>([]);
  const [sessionExpiry, setSessionExpiry] = useState<Date | null>(null);

  useEffect(() => {
    // Calculate session expiry from JWT
    if (session?.expires_at) {
      setSessionExpiry(new Date(session.expires_at * 1000));
    } else {
      setSessionExpiry(null);
    }
  }, [session]);

  useEffect(() => {
    // Track auth events locally
    if (lastAuthEvent) {
      setAuthEvents(prev => [
        { 
          event: lastAuthEvent.event, 
          timestamp: new Date(lastAuthEvent.timestamp),
          userId: lastAuthEvent.userId 
        },
        ...prev.slice(0, 9) // Keep last 10 events
      ]);
    }
  }, [lastAuthEvent]);

  const handleRefreshSession = async () => {
    setRefreshing(true);
    try {
      const { error } = await supabase.auth.refreshSession();
      if (error) {
        console.error('Error refreshing session:', error);
      }
    } catch (err) {
      console.error('Refresh error:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const getRoleBadgeVariant = (role: string | null): "default" | "secondary" | "destructive" | "outline" => {
    switch (role) {
      case 'superadmin': return 'default';
      case 'admin': return 'secondary';
      case 'editor': return 'outline';
      default: return 'destructive';
    }
  };

  const getEventIcon = (event: string) => {
    switch (event) {
      case 'SIGNED_IN':
      case 'TOKEN_REFRESHED':
        return <CheckCircle className="h-3 w-3 text-emerald-500" />;
      case 'SIGNED_OUT':
        return <XCircle className="h-3 w-3 text-red-500" />;
      case 'USER_UPDATED':
      case 'PASSWORD_RECOVERY':
        return <AlertCircle className="h-3 w-3 text-amber-500" />;
      default:
        return <Activity className="h-3 w-3 text-muted-foreground" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Current Auth State */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Estado de Autenticación
          </CardTitle>
          <CardDescription>
            Información en tiempo real sobre tu sesión y permisos
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Loading State Indicator */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Estado del contexto:</span>
            </div>
            <div className="flex items-center gap-2">
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin text-amber-500" />
                  <span className="text-sm text-amber-600">Cargando...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                  <span className="text-sm text-emerald-600">Listo</span>
                </>
              )}
            </div>
          </div>

          {/* User Info */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-3">
              <h4 className="text-sm font-medium flex items-center gap-2">
                <User className="h-4 w-4" />
                Usuario Actual
              </h4>
              <div className="p-3 rounded-lg bg-muted/50 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Email:</span>
                  <span className="font-mono text-xs">{user?.email || 'N/A'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">ID:</span>
                  <span className="font-mono text-xs truncate max-w-[200px]" title={user?.id}>
                    {user?.id ? `${user.id.slice(0, 8)}...` : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Creado:</span>
                  <span className="text-xs">
                    {user?.created_at 
                      ? format(new Date(user.created_at), 'dd/MM/yyyy HH:mm') 
                      : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="text-sm font-medium flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Rol y Permisos
              </h4>
              <div className="p-3 rounded-lg bg-muted/50 space-y-2">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Rol detectado:</span>
                  <Badge variant={getRoleBadgeVariant(role)}>
                    {role || 'Sin rol'}
                  </Badge>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Es admin:</span>
                  {isAdmin ? (
                    <Badge variant="default" className="bg-emerald-500">Sí</Badge>
                  ) : (
                    <Badge variant="destructive">No</Badge>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Session Info */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Sesión
            </h4>
            <div className="p-3 rounded-lg bg-muted/50 space-y-2">
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground">Estado:</span>
                {session ? (
                  <Badge variant="default" className="bg-emerald-500">Activa</Badge>
                ) : (
                  <Badge variant="destructive">Sin sesión</Badge>
                )}
              </div>
              {sessionExpiry && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Expira:</span>
                  <span className="text-xs">
                    {format(sessionExpiry, 'dd/MM/yyyy HH:mm:ss')} 
                    <span className="text-muted-foreground ml-1">
                      ({formatDistanceToNow(sessionExpiry, { addSuffix: true })})
                    </span>
                  </span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Provider:</span>
                <span className="font-mono text-xs">
                  {user?.app_metadata?.provider || 'email'}
                </span>
              </div>
            </div>
          </div>

          {/* Refresh Button */}
          <Button 
            onClick={handleRefreshSession} 
            disabled={refreshing || !session}
            variant="outline"
            className="w-full"
          >
            {refreshing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Refrescando...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refrescar Sesión
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Auth Events Log */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Últimos Eventos de Auth
          </CardTitle>
          <CardDescription>
            Historial de eventos de autenticación en esta sesión
          </CardDescription>
        </CardHeader>
        <CardContent>
          {authEvents.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No hay eventos recientes</p>
              <p className="text-xs mt-1">Los eventos aparecerán aquí conforme ocurran</p>
            </div>
          ) : (
            <div className="space-y-2">
              {authEvents.map((event, index) => (
                <div 
                  key={`${event.event}-${event.timestamp.getTime()}-${index}`}
                  className="flex items-center justify-between p-2 rounded-lg bg-muted/30 text-sm"
                >
                  <div className="flex items-center gap-2">
                    {getEventIcon(event.event)}
                    <span className="font-mono text-xs">{event.event}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {formatDistanceToNow(event.timestamp, { addSuffix: true })}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuthDebugSection;
